import { Grid } from '@mui/material';
import { getIn } from 'formik';

import { MONTH_YEAR_DISPLAY_FORMAT } from '../../../../constants';
import { disabledFutureDate } from '../../../../helpers';
import {
  FormAutocompleteField,
  FormDateField,
  FormNumberField,
  FormPhoneField,
  FormTextField,
} from '../../components';
import {
  CHILD_ATTITUDE_TOWARDS_SCHOOL_OPTIONS,
  CLASSROOM_SETTING_OPTIONS,
  EDUCATION_EVALUATION_STATUS_OPTIONS,
  EDUCATION_HOURS_PER_WEEK_BOUNDS,
  EDUCATION_SETTING_HOMESCHOOL,
  EDUCATION_SETTING_OPTIONS,
  IEP_OR_504_OPTIONS,
} from '../constants';
import EducationDescriptionField from './EducationDescriptionField';

import type { EducationFieldsProps } from './types';

export default function EducationFields({
  formik,
  disabled,
  readOnly,
  saveField,
  richTextDescriptions = false,
  evaluationDatePicker,
}: EducationFieldsProps) {
  const isHomeschool = getIn(formik.values, 'education_setting') === EDUCATION_SETTING_HOMESCHOOL;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Education setting"
          name="education_setting"
          onChange={saveField}
          options={EDUCATION_SETTING_OPTIONS}
          placeholder="Select education setting"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormNumberField
          disabled={disabled}
          formik={formik}
          label="Number of hours per week"
          max={EDUCATION_HOURS_PER_WEEK_BOUNDS.max}
          maxDecimalPlaces={EDUCATION_HOURS_PER_WEEK_BOUNDS.decimalPlaces}
          min={EDUCATION_HOURS_PER_WEEK_BOUNDS.inputMin}
          name="number_of_hours_per_week"
          onBlur={saveField}
          placeholder="Enter number of hours"
          readOnly={readOnly}
          required
          step={EDUCATION_HOURS_PER_WEEK_BOUNDS.step}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="School name"
          name="school_name"
          onBlur={saveField}
          placeholder="Enter school name"
          readOnly={readOnly}
          required={!isHomeschool}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormPhoneField
          disabled={disabled}
          formik={formik}
          label="School phone number"
          name="school_phone_number"
          onBlur={saveField}
          placeholder="Enter phone number"
          readOnly={readOnly}
          required={!isHomeschool}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <EducationDescriptionField
          disabled={disabled}
          formik={formik}
          label="Education description"
          name="education_description"
          placeholder="Enter education description"
          readOnly={readOnly}
          richText={richTextDescriptions}
          saveField={saveField}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <EducationDescriptionField
          disabled={disabled}
          formik={formik}
          label="Focus area description"
          name="focus_area_description"
          placeholder="Enter focus area"
          readOnly={readOnly}
          richText={richTextDescriptions}
          saveField={saveField}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Client's education evaluation status"
          name="client_education_evaluation_status"
          onChange={saveField}
          options={EDUCATION_EVALUATION_STATUS_OPTIONS}
          placeholder="Select education evaluation status"
          readOnly={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormDateField
          allowClear
          disabled={disabled}
          disabledDate={disabledFutureDate}
          formik={formik}
          format={evaluationDatePicker === 'month' ? MONTH_YEAR_DISPLAY_FORMAT : undefined}
          label="Evaluation date"
          name="client_education_evaluation_date"
          onChange={saveField}
          picker={evaluationDatePicker}
          placeholder={
            evaluationDatePicker === 'month' ? MONTH_YEAR_DISPLAY_FORMAT : 'Select evaluation date'
          }
          readOnly={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="IEP or 504 in place"
          name="iep_or_504_in_place"
          onChange={saveField}
          options={IEP_OR_504_OPTIONS}
          placeholder="Select IEP or 504"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Educational support"
          name="education_support"
          onBlur={saveField}
          placeholder="Describe educational support"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Child's current classroom setting"
          name="client_classroom_setting"
          onChange={saveField}
          options={CLASSROOM_SETTING_OPTIONS}
          placeholder="Select classroom setting"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Child's attitude towards school"
          name="client_attitude_towards_school"
          onChange={saveField}
          options={CHILD_ATTITUDE_TOWARDS_SCHOOL_OPTIONS}
          placeholder="Select child attitude"
          readOnly={readOnly}
          required
        />
      </Grid>
    </Grid>
  );
}
